import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const HotelOccupancyRate = () => {
  // State variables for occupancy rate calculation
  const [roomsSold, setRoomsSold] = useState(0); // Number of rooms sold
  const [totalRooms, setTotalRooms] = useState(0); // Total number of rooms available
  const [occupancyRate, setOccupancyRate] = useState(0); // Calculated occupancy rate

  // Event handlers for input changes
  const handleRoomsSoldChange = (e) =>
    setRoomsSold(parseInt(e.target.value) || 0);
  const handleTotalRoomsChange = (e) =>
    setTotalRooms(parseInt(e.target.value) || 0);

  // useEffect to calculate occupancy rate
  useEffect(() => {
    if (totalRooms > 0) {
      const rate = (roomsSold / totalRooms) * 100; // Calculating occupancy rate
      setOccupancyRate(rate);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [roomsSold, totalRooms]);

  return (
    <Layout title="Hotel Occupancy Rate Calculator" path="hotel-occupancy">
      <section className="section-padding" id="hotel-occupancy">
        <div className="container mt-5">
          <div className="row">
            {/* Hotel Occupancy Rate Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Hotel Occupancy Rate Calculator
                </h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Rooms Sold */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="roomsSold" className="form-label">
                        Rooms Sold
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Rooms Sold:</strong> The number of rooms that have been sold or booked by guests during a specific period."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="roomsSold"
                        placeholder="Enter number of rooms sold"
                        value={roomsSold || ""}
                        onChange={handleRoomsSoldChange}
                      />
                    </div>

                    {/* Input for Total Rooms Available */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalRooms" className="form-label">
                        Total Rooms Available
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Total Rooms Available:</strong> The total number of rooms in the hotel that are available for booking."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalRooms"
                        placeholder="Enter total number of rooms available"
                        value={totalRooms || ""}
                        onChange={handleTotalRoomsChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Occupancy Rate:{" "}
                        <span className="fw-bold ms-3">
                          {occupancyRate === 0
                            ? "0"
                            : occupancyRate.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                          %
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to Hotel Occupancy Rate Calculation
                </h3>

                {/* What is Occupancy Rate? */}
                <h4 className="mt-3 mb-3">What is Occupancy Rate?</h4>
                <p>
                  **Occupancy Rate** refers to the percentage of available rooms
                  that have been sold. It is a key performance indicator for
                  hotels, resorts, and other accommodation providers.
                </p>

                {/* Why Use the Hotel Occupancy Rate Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use the Hotel Occupancy Rate Calculator?
                </h4>
                <p>
                  This calculator allows hotel owners, managers, and analysts to
                  quickly determine the occupancy rate, helping them forecast
                  demand, optimize pricing strategies, and manage resources
                  efficiently.
                </p>

                {/* Benefits of Using the Hotel Occupancy Rate Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using the Hotel Occupancy Rate Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Forecasting Demand:</strong> Helps predict booking
                    trends based on occupancy rates.
                  </li>
                  <li>
                    <strong>Optimizing Room Pricing:</strong> Enables dynamic
                    pricing adjustments based on occupancy levels.
                  </li>
                  <li>
                    <strong>Efficient Resource Management:</strong> Assists in
                    staffing and service planning based on occupancy.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use the Hotel Occupancy Rate Calculator?
                </h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Hotel Owners & Managers:</strong> To monitor and
                    manage occupancy trends.
                  </li>
                  <li>
                    <strong>Resorts & Lodging Providers:</strong> To track
                    performance and plan for future growth.
                  </li>
                  <li>
                    <strong>Travel Industry Analysts:</strong> For analyzing
                    hotel performance and industry trends.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h5 className="mt-3 mb-2">
                  Why is the occupancy rate important?
                </h5>
                <p>
                  The occupancy rate is important because it directly affects a
                  hotel's revenue and profitability. A higher occupancy rate
                  indicates better demand and efficient room utilization.
                </p>

                <h5 className="mt-3 mb-2">
                  How can I improve my hotel’s occupancy rate?
                </h5>
                <p>
                  Strategies to improve occupancy include targeted marketing
                  campaigns, offering seasonal promotions, improving online
                  reviews, and using dynamic pricing models.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HotelOccupancyRate;
